import { formatMoneyValue, parseMoney } from './money.js'

/**
 * Sales tax, as a percent.
 *
 * Carried as an exact decimal string ("8.25", "8.875") and computed in
 * integer thousandths of a percent, for the same reason money is computed
 * in cents: a rate that is nearly 8.25 is how a total ends up nearly right.
 *
 * One rounding, on the proposal total. Taxing each line and summing would
 * round ninety times and land somewhere near the answer; the figure a board
 * checks by hand is the rate times the total, rounded once.
 */

/** 1% is 1000 thousandths of a percent, so 8.25% is 8250. */
const THOUSANDTHS_PER_PERCENT = 1000

/** 100% in those thousandths. A rate above this is a typo, not a tax. */
const MAX_RATE_THOUSANDTHS = 100 * THOUSANDTHS_PER_PERCENT

/**
 * The divisor that turns "cents times thousandths of a percent" into cents
 * of tax. 8.25% is 8250 / 100000.
 */
const TAX_DENOMINATOR = 100 * THOUSANDTHS_PER_PERCENT

/**
 * Parse a percent into integer thousandths, or null if it is not one.
 *
 * Up to three decimal places and no higher than 100. "8.25" and "8.250" are
 * the same rate, which is what lets a settings field know that the server
 * echoing a normalized string back is not an unsaved change.
 */
export function parseTaxRatePercent(value: string | null): number | null {
  if (value === null) return null
  const match = /^(\d{1,3})(?:\.(\d{1,3}))?$/.exec(value.trim())
  if (match === null) return null
  const whole = match[1]
  const fraction = match[2] ?? ''
  if (whole === undefined) return null
  const thousandths =
    Number(whole) * THOUSANDTHS_PER_PERCENT + Number(fraction.padEnd(3, '0'))
  if (!Number.isSafeInteger(thousandths)) return null
  if (thousandths > MAX_RATE_THOUSANDTHS) return null
  return thousandths
}

/**
 * A percent as someone would write it: "8.25", never "8.250".
 *
 * The column keeps three decimal places so 8.875% is exact. The trailing
 * zeros are storage, and a proposal that prints "8.250%" looks like the
 * rate was measured rather than chosen.
 */
export function formatTaxRatePercent(value: string): string | null {
  const thousandths = parseTaxRatePercent(value)
  if (thousandths === null) return null
  const whole = Math.floor(thousandths / THOUSANDTHS_PER_PERCENT)
  const fraction = thousandths % THOUSANDTHS_PER_PERCENT
  if (fraction === 0) return String(whole)
  const digits = String(fraction).padStart(3, '0').replace(/0+$/, '')
  return `${String(whole)}.${digits}`
}

/** What tax does to one pre-tax total. */
export interface TaxedAmount {
  /**
   * The tax itself, or null when none is added.
   *
   * Null covers three states that print the same way: no rate configured,
   * a rate of zero, and a total that was never priced. A tax line of $0.00
   * would be a row explaining a decision the document does not need to make.
   */
  taxAmount: string | null
  /**
   * What the client pays.
   *
   * Equals the subtotal when no tax is added, and null when the subtotal
   * itself is null — an unpriced proposal is not a proposal for $0 plus tax.
   */
  amountDue: string | null
}

/**
 * Tax on a pre-tax total.
 *
 * `subtotal` is an exact decimal string, the same shape every other price
 * uses. The rate is a percent. Both round through integers, once.
 */
export function taxOn(
  subtotal: string | null,
  taxRatePercent: string | null,
): TaxedAmount {
  return taxOnPortion(subtotal, subtotal, taxRatePercent)
}

/**
 * Tax on the taxable part of a pre-tax total.
 *
 * Irrigation repairs are taxed item by item, as Housecall Pro marks them:
 * commercial labor is taxed and residential labor is not, and nearly every
 * part is. So a total can be partly taxable, and only that part is taxed,
 * still once and still on a total rather than per item. A taxable portion
 * that cannot be read is treated as none of it.
 */
export function taxOnPortion(
  subtotal: string | null,
  taxable: string | null,
  taxRatePercent: string | null,
): TaxedAmount {
  if (subtotal === null) return { taxAmount: null, amountDue: null }
  const cents = parseMoney(subtotal)
  if (cents === null) return { taxAmount: null, amountDue: null }

  const rate = parseTaxRatePercent(taxRatePercent)
  const taxableCents = parseMoney(taxable) ?? 0
  if (rate === null || rate === 0) {
    return { taxAmount: null, amountDue: formatMoneyValue(cents) }
  }

  const tax = Math.round((taxableCents * rate) / TAX_DENOMINATOR)
  return {
    taxAmount: formatMoneyValue(tax),
    amountDue: formatMoneyValue(cents + tax),
  }
}
